import { useState, useCallback, useEffect } from 'react';
import { useBroadcastProviderNotifier } from '@bizzabo/broadcast';
import config from '../../../../config/config';
import { getAccessTokenName } from '../../../../AuthToken';
import { getDomainAdjustedPath } from '../../../../bizzaboAjax';
import FeatureToggleUtils from 'FeatureToggleUtils';

export const useBroadcastProviderManager = ({
    sessionId,
    eventId,
    accountId,
    virtualSessionDetails,
    enabled = true,
}) => {
    const [availableProviders, setAvailableProviders] = useState([]);
    const [currentBroadcastProvider, setCurrentBroadcastProvider] = useState(null);

    const refreshTokenUrl = getDomainAdjustedPath('/userProfile/oauth/refreshToken');

    useEffect(() => {
        if (virtualSessionDetails?.broadcastAccess) {
            setAvailableProviders(virtualSessionDetails.broadcastAccess);
            setCurrentBroadcastProvider(
                virtualSessionDetails.currentBroadcastProvider ||
                virtualSessionDetails.videoProviderType
            );
        }
    }, [virtualSessionDetails]);

    const handleProviderAdded = useCallback((streamDetails) => {
        setAvailableProviders((currentProviders) => {
            const exists = currentProviders.some(
                (provider) => provider.attendeeUrl === streamDetails.attendeeUrl
            );
            if (exists) {
                return currentProviders.map((provider) =>
                    provider.attendeeUrl === streamDetails.attendeeUrl
                        ? streamDetails
                        : provider
                );
            }
            return [...currentProviders, streamDetails];
        });
    }, []);

    const handleProviderRemoved = useCallback((videoProviderType) => {
        setAvailableProviders((currentProviders) =>
            currentProviders.filter(
                (provider) => provider.videoProviderType !== videoProviderType
            )
        );
    }, []);

    useBroadcastProviderNotifier({
        sessionId: String(sessionId),
        bizzaboStreamId: virtualSessionDetails?.bizzaboStreamId,
        accountId,
        eventId: String(eventId),
        currentStreamDetails: availableProviders,
        currentBroadcastProvider: currentBroadcastProvider,
        handlers: {
            onBroadcastProviderAdded: handleProviderAdded,
            onBroadcastProviderRemoved: handleProviderRemoved,
            onBroadcastProviderChanged: setCurrentBroadcastProvider,
        },
        apiUrl: config.apiServer + '/',
        realtimeNotificationsHost: config.realtimeNotificationsHost,
        accessTokenCookieName: getAccessTokenName(),
        refreshTokenUrl,
        aggressiveRestartMode: FeatureToggleUtils.isFeatureEnabled('realtime-notifier-aggressive-mode'),
        fallbackUrl: [
            `/api/v2/agenda/events/${eventId}/sessions/${sessionId}/virtualSession`,
        ],
        clientFacing: true,
        enabled,
    });

    return {
        availableProviders,
        currentBroadcastProvider,
    };
};
